import styled, { css, FlattenInterpolation, ThemedStyledProps } from 'styled-components/macro';
import { deriveMaxWidth } from '../utils';
import { PopoverSizes } from '../enums/PopoverSizes';
import { DeviceSizes } from 'shared/enums/DeviceSizes';
import { PopoverName } from 'shared/enums/PopoverName';
import { RouteBasePaths } from 'routes';

interface IStyledContentProps {
  size: PopoverSizes;
  left: number;
  top: number;
  name?: string;
  isMobile?: boolean;
  isTouchDevice?: boolean;
  width?: number;
  height?: number;
  route?: string;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const defaultStyles = css<IStyledContentProps>`
  border-radius: 10px;
  box-shadow: 0 2px 21px 11px rgba(0, 0, 0, 0.04);
  left: ${({ left }: IStyledContentProps): string => `${left}px`};
  max-width: ${({ size }: IStyledContentProps): string => `${deriveMaxWidth({ size })}`};
  width: ${({ width }): string => (!!width ? `${width}px` : '60%')};
  top: ${({ top }: IStyledContentProps): string => `${top}px`};
  ${({
    name,
    isTouchDevice,
  }: IStyledContentProps): FlattenInterpolation<ThemedStyledProps<IStyledContentProps, any>> | null => {
    return isTouchDevice && name === PopoverName.PhoneCall ? touchDevicePhoneCallStyles : null;
  }};
`;

const tabletStyles = css<IStyledContentProps>`
  right: 0px;
  cursor: pointer;
  height: ${({ name, height, isMobile }: IStyledContentProps): string => {
    const defaultHeight = height ? `${height}px` : '85px';
    switch (name) {
      case PopoverName.VideoCall:
        return '100vh';
      case PopoverName.PhoneCall:
        return '130px';
      case PopoverName.UnsupportedBrowser:
        return isMobile ? defaultHeight : 'auto';
      default:
        return defaultHeight;
    }
  }};
`;

const touchDevicePhoneCallStyles = css<IStyledContentProps>`
  width: calc(100% - 134px);
  max-width: 100%;
  bottom: 1px;
  top: unset;
  left: 70px;
  border-radius: 0;
`;

/* eslint-disable @typescript-eslint/no-explicit-any */
const mobileStyles = css<IStyledContentProps>`
  border-radius: 10px 10px 0 0;
  box-shadow: 0 -10px 21px 0px rgb(0 0 0 / 4%);
  left: 0;
  max-width: 100%;
  width: 100%;
  ${({ name }: IStyledContentProps): FlattenInterpolation<ThemedStyledProps<IStyledContentProps, any>> => {
    switch (name) {
      case PopoverName.VideoCall:
        return mobileVideoCallStyles;
      case PopoverName.PhoneCall:
        return mobilePhoneCallStyles;
      default:
        return [];
    }
  }}
`;
/* eslint-enable @typescript-eslint/no-explicit-any */

const mobileVideoCallStyles = css<IStyledContentProps>`
  top: 0;
  position: inherit;
`;

const mobilePhoneCallStyles = css<IStyledContentProps>`
  top: auto;
  bottom: ${({ route }: IStyledContentProps): string => {
    // put phone call popover on top of bottom toolbar or message composer, depending on the page
    if (route?.startsWith(`${RouteBasePaths.Team}/`)) return '216px';
    if (route?.startsWith(`${RouteBasePaths.Conversations}/`)) return '194px';
    return '64px';
  }};
`;

/* eslint-disable @typescript-eslint/no-explicit-any */
const StyledContent = styled.div<IStyledContentProps>`
  background-color: var(--chakra-colors-background-default);
  border: solid 1px #efefef;
  cursor: move;
  overflow: hidden;
  pointer-events: auto;
  position: fixed;
  bottom: auto;
  z-index: 5000;
  ${({ isMobile }: IStyledContentProps): FlattenInterpolation<ThemedStyledProps<IStyledContentProps, any>> => {
    return isMobile ? mobileStyles : defaultStyles;
  }};
  @media (${DeviceSizes.TabletMaxWidth}) {
    ${tabletStyles}
  }
`;
/* eslint-enable @typescript-eslint/no-explicit-any */

export default StyledContent;
